/* eslint-disable complexity */
import {useInfiniteQuery} from '@tanstack/react-query'
import _ from 'lodash'
import React, {useEffect} from 'react'

import {api} from '../../../../App.global'
import {OrderIntakeSite, isSearchQueryIncludedInString} from '../types/OrderIntakeSite.types'

const ITEMS_PER_PAGE = 50
const MIN_SEARCH_TERM_LENGTH = 3

export const fetchSites = async (
  page: number,
  searchTerm?: string
): Promise<Array<OrderIntakeSite>> => {
  const limit = ITEMS_PER_PAGE
  const startIndex = (page - 1) * limit
  const skip = startIndex < 0 ? 0 : startIndex

  const params = (() => {
    if (searchTerm && searchTerm.length > 0) {
      return {searchTerm}
    }
    return {limit, skip}
  })()

  const response = await api.get('/orderRequests/sites', {params})
  const {data} = response
  return data
}

export const useOrderRequestSites = (searchTerm?: string) => {
  const [searchTermForRequest, setSearchTermForRequest] = React.useState(searchTerm)
  const sitesSearchingEnabled = !!searchTermForRequest && searchTermForRequest.length > 0
  const sitesFetchingEnabled = !sitesSearchingEnabled

  useEffect(() => {
    if (!!searchTerm && searchTerm.length >= MIN_SEARCH_TERM_LENGTH) {
      const newSearchTerm = searchTerm.slice(0, MIN_SEARCH_TERM_LENGTH)
      if (newSearchTerm === searchTermForRequest) {
        return
      }
      setSearchTermForRequest(newSearchTerm)
      return
    }

    if (
      sitesFetchingEnabled ||
      searchTerm?.length === 0 ||
      (searchTerm !== undefined && searchTerm?.length < (searchTermForRequest?.length || 0))
    ) {
      setSearchTermForRequest('')
      return
    }
  }, [searchTerm, sitesFetchingEnabled, sitesSearchingEnabled, searchTermForRequest])

  const fetchingQueryInfo = useInfiniteQuery({
    queryKey: ['orderRequestsSites'],
    queryFn: ({pageParam = 1}: {pageParam?: number}) => {
      return fetchSites(pageParam)
    },
    getNextPageParam: (lastPage: OrderIntakeSite[], allPages: OrderIntakeSite[][]) => {
      return lastPage.length === ITEMS_PER_PAGE ? allPages.length + 1 : undefined
    },
    enabled: sitesFetchingEnabled,
    cacheTime: 90 * 1000,
    staleTime: 90 * 1000
  })

  const searchingQueryInfo = useInfiniteQuery({
    queryKey: ['orderRequestsSites', {searchTermForRequest}],
    queryFn: ({pageParam = 1}: {pageParam?: number}) => {
      return fetchSites(pageParam, searchTermForRequest)
    },
    getNextPageParam: () => undefined,
    enabled: sitesSearchingEnabled,
    cacheTime: 90 * 1000,
    staleTime: 90 * 1000
  })

  // If the search query is included in the previous search query, we can filter the data locally
  if (
    searchingQueryInfo.data &&
    searchTermForRequest &&
    searchTerm &&
    searchTerm?.length > searchTermForRequest.length &&
    searchTerm?.includes(searchTermForRequest)
  ) {
    const {data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage} = searchingQueryInfo
    const dataCloned = _.cloneDeep(data)
    dataCloned.pages.forEach((page, index) => {
      dataCloned.pages[index] = page.filter((site: OrderIntakeSite) => {
        return (
          isSearchQueryIncludedInString(site.siteName, searchTerm.toLocaleLowerCase()) ||
          isSearchQueryIncludedInString(site.city, searchTerm.toLocaleLowerCase()) ||
          isSearchQueryIncludedInString(site.street, searchTerm.toLocaleLowerCase())
        )
      })
    })
    return {
      data: dataCloned,
      fetchNextPage,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
      isSearching: true,
      isLoading: false
    }
  }

  const isLoading = searchingQueryInfo.isLoading || fetchingQueryInfo.isLoading
  const isFetching = searchingQueryInfo.isFetching || fetchingQueryInfo.isFetching

  if (
    searchTerm !== undefined &&
    searchTermForRequest &&
    searchTerm.length < (searchTermForRequest?.length || 0)
  ) {
    return {...fetchingQueryInfo, isLoading, isFetching, isSearching: false}
  }

  return searchingQueryInfo.data || searchingQueryInfo.isLoading || searchingQueryInfo.isFetching
    ? {...searchingQueryInfo, isLoading, isFetching, isSearching: true}
    : {...fetchingQueryInfo, isLoading, isFetching, isSearching: false}
}

export const warmupSitesEndpoint = async () => {
  await api.head('/orderRequests/sites')
}
